import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Input, Stack, Typography } from '@org/design-system';
import { AlertTriangle, Check, Copy } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { generateShadcnAppConfig } from '../generateShadcnAppConfig';
import type { ShadcnOpenApiSpec } from '../types';
import {
  discoverEntities,
  scaffoldCrudListPage,
  validatePagesAgainstApi,
  type BuilderPageConfig,
  type DiscoveredEntity,
} from './openapi-utils';

interface ConfigBuilderProps {
  initialApiBaseUrl: string;
}

type FieldOverrideState = Record<string, { override: boolean; type: 'text' | 'email' | 'number' | 'select' }>;
type BuilderTab = 'builder' | 'query-performance';

interface QueryPerformancePoint {
  date: string;
  tenantId: string;
  entity: string;
  p50: number;
  p95: number;
  p99: number;
}

interface LayoutPerformanceRow {
  layoutType: string;
  avgActualDuration: number;
}

const COMPARISON_LAYOUT_TYPES = ['kanban', 'analytics-grid', 'split-panel'] as const;

type ThemeField = 'primary' | 'secondary' | 'background' | 'foreground' | 'muted' | 'accent' | 'radius';
type ThemeValues = Record<ThemeField, string>;

const COLOR_FIELDS: Array<Exclude<ThemeField, 'radius'>> = ['primary', 'secondary', 'background', 'foreground', 'muted', 'accent'];

const STYLE_THEME_PRESETS: Array<{ id: string; label: string; values: ThemeValues }> = [
  {
    id: 'default',
    label: 'Default',
    values: {
      primary: '#6366f1',
      secondary: '#f2f0ec',
      background: '#fcfaf9',
      foreground: '#1d2030',
      muted: '#f2f0ec',
      accent: '#efedfa',
      radius: '0.5rem',
    },
  },
  {
    id: 'aero-mica',
    label: 'Aero Mica',
    values: {
      primary: '#1d8bf3',
      secondary: '#eceff4',
      background: '#f4f6fa',
      foreground: '#192338',
      muted: '#eceff4',
      accent: '#daf3fa',
      radius: '0.75rem',
    },
  },
  {
    id: 'executive',
    label: 'Executive',
    values: {
      primary: '#1d356f',
      secondary: '#f0f3f8',
      background: '#f8fafd',
      foreground: '#141f3c',
      muted: '#e4e9f0',
      accent: '#f4e6b9',
      radius: '0.5rem',
    },
  },
  {
    id: 'glass-luxe',
    label: 'Glass Luxe',
    values: {
      primary: '#7a47f1',
      secondary: '#edf1fa',
      background: '#f5f7fd',
      foreground: '#1f2240',
      muted: '#edf1fa',
      accent: '#d8f2fc',
      radius: '1rem',
    },
  },
  {
    id: 'minimal-contrast',
    label: 'Minimal Contrast',
    values: {
      primary: '#2557f2',
      secondary: '#fafbfc',
      background: '#ffffff',
      foreground: '#111827',
      muted: '#f1f4f8',
      accent: '#e6efff',
      radius: '0.375rem',
    },
  },
  {
    id: 'neo-dark',
    label: 'Neo Dark',
    values: {
      primary: '#08deff',
      secondary: '#181c2b',
      background: '#090c14',
      foreground: '#dee8f5',
      muted: '#181c2b',
      accent: '#bc54f1',
      radius: '0.5rem',
    },
  },
  {
    id: 'retrofuture',
    label: 'Retrofuture',
    values: {
      primary: '#9f3cf9',
      secondary: '#e6eaf5',
      background: '#f2f3f9',
      foreground: '#1a1e3a',
      muted: '#e6eaf5',
      accent: '#d2f7fd',
      radius: '0.75rem',
    },
  },
  {
    id: 'maximal-luxe',
    label: 'Maximal Luxe',
    values: {
      primary: '#d11a68',
      secondary: '#eee3db',
      background: '#f5ecea',
      foreground: '#3a2430',
      muted: '#e2d2c9',
      accent: '#f9dc84',
      radius: '1rem',
    },
  },
  {
    id: 'neumorphism',
    label: 'Neumorphism',
    values: {
      primary: '#d0d9eb',
      secondary: '#e8edf5',
      background: '#d7deea',
      foreground: '#4a5974',
      muted: '#ebeff6',
      accent: '#ced8e8',
      radius: '1rem',
    },
  },
  {
    id: 'brutalist',
    label: 'Brutalist',
    values: {
      primary: '#0044e6',
      secondary: '#e6e6e6',
      background: '#fafafa',
      foreground: '#0d0d0d',
      muted: '#e6e6e6',
      accent: '#ffe119',
      radius: '0px',
    },
  },
  {
    id: 'editorial',
    label: 'Editorial',
    values: {
      primary: '#1a1a1a',
      secondary: '#f3eee7',
      background: '#fbf7f1',
      foreground: '#1a1a1a',
      muted: '#f7f2ec',
      accent: '#e8ddc8',
      radius: '0px',
    },
  },
  {
    id: 'illustration',
    label: 'Illustration',
    values: {
      primary: '#ff0080',
      secondary: '#fdeea5',
      background: '#ffeaf6',
      foreground: '#470d4f',
      muted: '#ffe0ef',
      accent: '#fced94',
      radius: '1rem',
    },
  },
];

function getContrastRatio(foreground: string, background: string): number | null {
  const f = hexToRgb(foreground);
  const b = hexToRgb(background);
  if (!f || !b) return null;

  const luminance = ({ r, g, b: blue }: { r: number; g: number; b: number }) => {
    const channel = (value: number) => {
      const normalized = value / 255;
      return normalized <= 0.03928 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(blue);
  };

  const l1 = luminance(f);
  const l2 = luminance(b);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

function hexToRgb(value: string): { r: number; g: number; b: number } | null {
  const normalized = value.trim().replace('#', '');
  const parsed = normalized.length === 3
    ? normalized.split('').map(char => char + char).join('')
    : normalized;

  if (!/^[0-9a-fA-F]{6}$/.test(parsed)) return null;

  return {
    r: Number.parseInt(parsed.slice(0, 2), 16),
    g: Number.parseInt(parsed.slice(2, 4), 16),
    b: Number.parseInt(parsed.slice(4, 6), 16),
  };
}

export function ConfigBuilder({ initialApiBaseUrl }: ConfigBuilderProps) {
  const [apiUrl, setApiUrl] = useState(initialApiBaseUrl);
  const [spec, setSpec] = useState<ShadcnOpenApiSpec | null>(null);
  const [pages, setPages] = useState<BuilderPageConfig[]>([]);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [validationStatus, setValidationStatus] = useState<Record<string, boolean>>({});
  const [fieldOverrides, setFieldOverrides] = useState<FieldOverrideState>({});
  const [activeTab, setActiveTab] = useState<BuilderTab>('builder');
  const [performanceData, setPerformanceData] = useState<QueryPerformancePoint[]>([]);
  const [performanceError, setPerformanceError] = useState<string | null>(null);
  const [layoutPerfRows, setLayoutPerfRows] = useState<LayoutPerformanceRow[]>([]);
const [themeValues, setThemeValues] = useState<ThemeValues>(STYLE_THEME_PRESETS[0].values);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const entities = useMemo(() => (spec ? discoverEntities(spec) : []), [spec]);
  const selectedPage = useMemo(() => pages.find(page => page.id === selectedPageId) ?? null, [pages, selectedPageId]);
  const selectedEntity = useMemo<DiscoveredEntity | null>(() => {
    if (!selectedPage?.entity) return null;
    return entities.find(entity => entity.name === selectedPage.entity) ?? null;
  }, [entities, selectedPage]);

  const contrastRatio = useMemo(() => getContrastRatio(themeValues.foreground, themeValues.background), [themeValues]);
  const hasAaContrast = (contrastRatio ?? 0) >= 4.5;

  const applyGeneratedConfig = (nextSpec: ShadcnOpenApiSpec, nextApiUrl: string) => {
    const generated = generateShadcnAppConfig(nextSpec, {
      appId: 'generated-app',
      apiBaseUrl: nextApiUrl,
    });

    const generatedPages: BuilderPageConfig[] = generated.pages.map((page, index) => ({
      ...page,
      id: page.id ?? `generated-${index}`,
      endpoint: page.path,
    }));

    setPages(generatedPages);
    setSelectedPageId(generatedPages[0]?.id ?? null);
  };

  const fetchSpec = async () => {
    setError(null);
    try {
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error(`Failed to load OpenAPI spec: ${response.status}`);
      const json = (await response.json()) as ShadcnOpenApiSpec;
      setSpec(json);
      applyGeneratedConfig(json, apiUrl);
    } catch (fetchError) {
      setError(fetchError instanceof Error ? fetchError.message : 'Unable to fetch OpenAPI spec.');
    }
  };

  const handleEntityDragStart = (event: React.DragEvent<HTMLButtonElement>, entity: DiscoveredEntity) => {
    event.dataTransfer.setData('application/entity-slug', entity.slug);
  };

  const handleCanvasDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const slug = event.dataTransfer.getData('application/entity-slug');
    const entity = entities.find(item => item.slug === slug);
    if (!entity) return;

    const newPage = scaffoldCrudListPage(entity);
    setPages(prev => [...prev, newPage]);
    setSelectedPageId(newPage.id);
  };

  const toggleField = (field: string, checked: boolean) => {
    if (!selectedPage) return;
    const existingColumns = selectedPage.table?.columns ?? [];
    const nextColumns = checked
      ? [...existingColumns, { key: field, header: field }]
      : existingColumns.filter(column => column.key !== field);

    setPages(prev => prev.map(page => (page.id === selectedPage.id
      ? {
          ...page,
          table: {
            ...(page.table ?? { columns: [], data: [] }),
            columns: nextColumns,
            data: page.table?.data ?? [],
          },
        }
      : page)));
  };

  const validateAgainstApi = async () => {
    const result = await validatePagesAgainstApi(apiUrl, pages);
    setValidationStatus(result);
  };

  const updateThemeField = (field: ThemeField, value: string) => {
    setThemeValues(prev => ({ ...prev, [field]: value }));
  };

  const applyThemePreset = (presetId: string) => {
    setSelectedThemePreset(presetId);
    const preset = STYLE_THEME_PRESETS.find(item => item.id === presetId);
    if (!preset) return;
    setThemeValues(preset.values);
  };

  const handleCopyThemeJson = async () => {
    const payload = {
      theme: {
        primary: themeValues.primary,
        secondary: themeValues.secondary,
        background: themeValues.background,
        foreground: themeValues.foreground,
        muted: themeValues.muted,
        accent: themeValues.accent,
        radius: themeValues.radius,
      },
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      setCopyMessage('Theme JSON copied');
    } catch {
      setCopyMessage('Clipboard unavailable');
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-12 gap-4 bg-background p-4">
      <Card className="col-span-12 p-4">
        <Stack gap={3}>
          <Typography variant="h3">Config Builder</Typography>
          <div className="flex gap-2">
            <Input
              value={apiUrl}
              onChange={event => setApiUrl(event.target.value)}
              placeholder="https://api.example.com/openapi.json"
              aria-label="OpenAPI URL"
            />
            <Button onClick={fetchSpec}>Fetch + Generate</Button>
            <Button variant="secondary" onClick={validateAgainstApi}>Validate against API</Button>
          </div>
          {error ? <Alert variant="error" title="Unable to fetch specification" description={error} /> : null}
        </Stack>
      </Card>

      <Card className="col-span-3 p-4">
        <Stack gap={3}>
          <Typography variant="h4">Entity Browser</Typography>
          {entities.map(entity => (
            <button
              key={entity.slug}
              type="button"
              draggable
              onDragStart={event => handleEntityDragStart(event, entity)}
              className="rounded-md border border-border bg-card p-2 text-left hover:bg-muted"
            >
              <Typography className="font-medium">{entity.name}</Typography>
              <Typography variant="small" color="muted">{entity.endpoint}</Typography>
            </button>
          ))}
        </Stack>
      </Card>

      <Card className="col-span-6 p-4">
        <Stack gap={3}>
          <Typography variant="h4">Canvas</Typography>
          <div
            className="min-h-72 rounded-md border border-dashed border-border p-3"
            onDragOver={event => event.preventDefault()}
            onDrop={handleCanvasDrop}
            style={{
              ['--primary' as string]: themeValues.primary,
              ['--secondary' as string]: themeValues.secondary,
              ['--background' as string]: themeValues.background,
              ['--foreground' as string]: themeValues.foreground,
              ['--muted' as string]: themeValues.muted,
              ['--accent' as string]: themeValues.accent,
              ['--radius' as string]: themeValues.radius,
            }}
          >
            {pages.length === 0 ? (
              <Typography color="muted">Drop an entity here to scaffold a CRUD list page.</Typography>
            ) : (
              <Stack gap={2}>
                {pages.map(page => (
                  <button
                    key={page.id}
                    type="button"
                    onClick={() => setSelectedPageId(page.id)}
                    className="flex items-center justify-between rounded border border-border bg-card px-3 py-2 text-left hover:bg-muted"
                  >
                    <span>{page.title ?? page.id}</span>
                    {validationStatus[page.id] === undefined ? null : (
                      <Badge variant={validationStatus[page.id] ? 'success' : 'destructive'}>
                        {validationStatus[page.id] ? 'Live' : 'Failed'}
                      </Badge>
                    )}
                  </button>
                ))}
              </Stack>
            )}
          </div>
        </Stack>
      </Card>

      <Card className="col-span-3 p-4">
        <Stack gap={4}>
          <Stack gap={3}>
            <Typography variant="h4">Property Editor</Typography>
            {!selectedPage || !selectedEntity ? (
              <Typography color="muted">Select a scaffolded entity page to edit fields.</Typography>
            ) : (
              <Stack gap={2}>
                {selectedEntity.fields.map(field => {
                  const checked = selectedPage.table?.columns.some(column => column.key === field.name) ?? false;
                  const overrideKey = `${selectedPage.id}:${field.name}`;
                  const overrideState = fieldOverrides[overrideKey] ?? {
                    override: false,
                    type: field.inferredType,
                  };

                  return (
                    <div key={field.name} className="rounded border border-border p-2">
                      <label className="flex items-center justify-between gap-2">
                        <span className="inline-flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={event => toggleField(field.name, event.target.checked)}
                          />
                          {field.name}
                        </span>
                        <Badge variant="secondary">{overrideState.override ? overrideState.type : field.inferredType}</Badge>
                      </label>
                      <Typography variant="small" color="muted">
                        {field.locked ? 'Inferred from schema (locked default).' : 'Inferred from schema.'}
                      </Typography>
                      <label className="mt-1 flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={overrideState.override}
                          onChange={event => {
                            setFieldOverrides(prev => ({
                              ...prev,
                              [overrideKey]: {
                                ...overrideState,
                                override: event.target.checked,
                              },
                            }));
                          }}
                        />
                        Override type
                      </label>
                      <select
                        className="mt-1 w-full rounded border border-input bg-background p-1"
                        value={overrideState.type}
                        disabled={!overrideState.override}
                        onChange={event => {
                          const type = event.target.value as 'text' | 'email' | 'number' | 'select';
                          setFieldOverrides(prev => ({
                            ...prev,
                            [overrideKey]: {
                              ...overrideState,
                              type,
                            },
                          }));
                        }}
                      >
                        <option value="text">text</option>
                        <option value="email">email</option>
                        <option value="number">number</option>
                        <option value="select">select</option>
                      </select>
                      {field.enumValues?.length ? (
                        <Typography variant="small" color="muted">
                          Options: {field.enumValues.join(', ')}
                        </Typography>
                      ) : null}
                    </div>
                  );
                })}
              </Stack>
            )}
          </Stack>

          <div className="border-t border-border pt-4">
            <Stack gap={3}>
              <Typography variant="h4">Theme Panel</Typography>

              <label className="space-y-1">
                <Typography variant="small" color="muted">Preset theme</Typography>
                <select
                  className="w-full rounded border border-input bg-background p-2 text-sm"
                  value={selectedThemePreset}
                  onChange={event => applyThemePreset(event.target.value)}
                  aria-label="Preset theme selector"
                >
                  {STYLE_THEME_PRESETS.map(preset => (
                    <option key={preset.id} value={preset.id}>{preset.label}</option>
                  ))}
                </select>
              </label>

              {COLOR_FIELDS.map(field => (
                <div key={field} className="space-y-2 rounded border border-border p-2">
                  <Typography variant="small" className="font-medium">--{field}</Typography>
                  <Input
                    value={themeValues[field]}
                    onChange={event => updateThemeField(field, event.target.value)}
                    aria-label={`CSS variable --${field}`}
                  />
                  <HexColorPicker color={themeValues[field]} onChange={value => updateThemeField(field, value)} />
                </div>
              ))}

              <label className="space-y-1">
                <Typography variant="small" className="font-medium">--radius</Typography>
                <Input
                  value={themeValues.radius}
                  onChange={event => updateThemeField('radius', event.target.value)}
                  aria-label="CSS variable --radius"
                />
              </label>

              <div className="flex items-center justify-between rounded border border-border p-2">
                <Typography variant="small">Foreground/Background contrast</Typography>
                <Badge variant={hasAaContrast ? 'success' : 'destructive'}>
                  {hasAaContrast ? <Check className="mr-1 h-3.5 w-3.5" /> : <AlertTriangle className="mr-1 h-3.5 w-3.5" />}
                  {contrastRatio ? `${contrastRatio.toFixed(2)}:1` : 'Invalid color'}
                </Badge>
              </div>
              {!hasAaContrast ? (
                <Typography variant="small" className="text-destructive">
                  WCAG AA requires at least 4.5:1 for normal text.
                </Typography>
              ) : null}

              <Button type="button" variant="secondary" onClick={handleCopyThemeJson}>
                <Copy className="mr-2 h-4 w-4" />
                Copy theme JSON
              </Button>
              {copyMessage ? <Typography variant="small" color="muted">{copyMessage}</Typography> : null}
            </Stack>
          </div>
        </Stack>
      </Card>
    </div>
  );
}
